import { Investments, InvestmentsDetails } from "../../pages/Investments";
import {
  canonicalOr,
  HistoryPointValuation,
  historyPointValuationOf,
} from "../valuation";
import {
  HistoryPointProvenance,
  InvestmentHistoryPoint,
  InvestmentHistoryResponse,
  InvestmentHistorySeries,
  InvestmentHistorySeriesPoint,
  InvestmentsHistoryPoint,
  InvestmentsHistoryResponse,
  InvestmentsResponse,
} from "./types";

/** Tolerance lives here alone: every domain type keeps its canonical keys required-but-nullable, so a response that drops one is still a compile error below `src/services/`. */
interface AccountCanonicalWire {
  brokerValue?: number | null;
  netGain?: number | null;
  quoteFetchedAt?: string | null;
  currentQuoteFetchedAt?: string | null;
}

type CanonicalAccountKeys = "brokerValue" | "netGain" | "quoteFetchedAt";

export interface InvestmentsWire
  extends Omit<Investments, CanonicalAccountKeys>, AccountCanonicalWire {}

export interface InvestmentsDetailsWire
  extends
    Omit<InvestmentsDetails, CanonicalAccountKeys>,
    AccountCanonicalWire {}

// `totalValue` before `currentValue`: on a pre-canonical ledger row `totalValue` is the D15 selection
// `brokerValue` became, and on a manual row it is absent-or-null so the account's own `currentValue` wins.
const toAccountRow = <Wire extends InvestmentsWire>(wire: Wire) => {
  // Unlike `toHolding`, the source keys stay: on a manual row they are the account's own fields, not aliases.
  const { currentQuoteFetchedAt, ...row } = wire;
  const account: InvestmentsWire = wire;
  return {
    ...row,
    brokerValue: canonicalOr(
      account,
      "brokerValue",
      "totalValue",
      "currentValue",
    ),
    netGain: canonicalOr(account, "netGain", "earnedValue"),
    quoteFetchedAt: canonicalOr(
      account,
      "quoteFetchedAt",
      "currentQuoteFetchedAt",
    ),
  };
};

export const toInvestments = (wire: InvestmentsWire): Investments =>
  toAccountRow(wire);

export const toInvestmentsDetails = (
  wire: InvestmentsDetailsWire,
): InvestmentsDetails => toAccountRow(wire);

export interface InvestmentsResponseWire extends Omit<
  InvestmentsResponse,
  "portfolioValue" | "investments" | "returnsByType"
> {
  portfolioValue?: number | null;
  currentValue?: number | null;
  investments?: InvestmentsWire[];
  returnsByType?: InvestmentsResponse["returnsByType"];
}

export const toInvestmentsResponse = (
  wire: InvestmentsResponseWire,
): InvestmentsResponse => {
  const { currentValue, ...response } = wire;
  return {
    ...response,
    portfolioValue: canonicalOr(wire, "portfolioValue", "currentValue"),
    investments: (wire.investments ?? []).map(toInvestments),
    returnsByType: wire.returnsByType ?? [],
  };
};

/** The point members Release B renames or may omit, shared by all three point types so one cannot be normalized differently. */
interface HistoryPointWire
  extends HistoryPointValuation, Partial<HistoryPointProvenance> {
  brokerValue?: number | null;
  value?: number | null;
}

type HistoryPointWireKeys =
  "brokerValue" | "valuation" | keyof HistoryPointProvenance;

const toHistoryPoint = <Wire extends HistoryPointWire>(wire: Wire) => {
  const { value, valuationState, valuationStateReason, valuationAsOf, ...row } =
    wire;
  const point: HistoryPointWire = wire;
  return {
    ...row,
    brokerValue: canonicalOr(point, "brokerValue", "value"),
    valuation: historyPointValuationOf(point),
    providerMarketDate: point.providerMarketDate ?? null,
    quoteFetchedAt: point.quoteFetchedAt ?? null,
    fxAsOfDate: point.fxAsOfDate ?? null,
    priceProvenances: point.priceProvenances ?? [],
    warnings: point.warnings ?? [],
  };
};

export type InvestmentsHistoryPointWire = Omit<
  InvestmentsHistoryPoint,
  HistoryPointWireKeys
> &
  HistoryPointWire;

export type InvestmentHistorySeriesPointWire = Omit<
  InvestmentHistorySeriesPoint,
  HistoryPointWireKeys
> &
  HistoryPointWire;

export type InvestmentHistoryPointWire = Omit<
  InvestmentHistoryPoint,
  HistoryPointWireKeys
> &
  HistoryPointWire;

export const toInvestmentsHistoryPoint = (
  wire: InvestmentsHistoryPointWire,
): InvestmentsHistoryPoint => toHistoryPoint(wire);

export const toInvestmentHistorySeriesPoint = (
  wire: InvestmentHistorySeriesPointWire,
): InvestmentHistorySeriesPoint => toHistoryPoint(wire);

export const toInvestmentHistoryPoint = (
  wire: InvestmentHistoryPointWire,
): InvestmentHistoryPoint => toHistoryPoint(wire);

export interface InvestmentHistorySeriesWire extends Omit<
  InvestmentHistorySeries,
  "points"
> {
  points?: InvestmentHistorySeriesPointWire[];
}

export interface InvestmentsHistoryResponseWire extends Omit<
  InvestmentsHistoryResponse,
  "data" | "byInvestment"
> {
  data?: InvestmentsHistoryPointWire[];
  byInvestment?: InvestmentHistorySeriesWire[];
}

export const toInvestmentsHistoryResponse = (
  wire: InvestmentsHistoryResponseWire,
): InvestmentsHistoryResponse => ({
  ...wire,
  data: (wire.data ?? []).map(toInvestmentsHistoryPoint),
  byInvestment: (wire.byInvestment ?? []).map((series) => ({
    ...series,
    points: (series.points ?? []).map(toInvestmentHistorySeriesPoint),
  })),
});

export interface InvestmentHistoryResponseWire extends Omit<
  InvestmentHistoryResponse,
  "points"
> {
  points?: InvestmentHistoryPointWire[];
}

export const toInvestmentHistoryResponse = (
  wire: InvestmentHistoryResponseWire,
): InvestmentHistoryResponse => ({
  ...wire,
  points: (wire.points ?? []).map(toInvestmentHistoryPoint),
});
